import type {MetaFunction, MetaDescriptor} from 'react-router'

/**
 * Remix drops parent meta tags when a child route exports its own `meta`.
 * This utility merges child meta with specific parent tags (og:image, twitter cards)
 * that should be present on every page.
 *
 * Usage:
 *   export const meta: MetaFunction = (args) => mergeMeta(args.matches, [
 *       {title: '...'},
 *       ...
 *   ])
 */

/**
 * Meta names a page may legitimately carry more than once, keyed by name+content instead of
 * name alone so the second one is not treated as a duplicate of the first. No product emits a
 * `rating` meta tag today (Ovid's own `meta.ratings`/`root.tsx` mechanism was removed 2026-09-17,
 * operator ruling — a clean surface carries no rating or adult meta); kept here in case a future
 * page legitimately needs the same name twice, not to special-case any one product.
 */
const REPEATABLE_META_NAMES = new Set(['rating'])

function metaKey(tag: MetaDescriptor): string | null {
    if ('property' in tag) return String(tag.property)
    if ('name' in tag) {
        const name = String(tag.name)
        return REPEATABLE_META_NAMES.has(name) ? `${name}:${String((tag as any).content)}` : name
    }
    if ('title' in tag) return '__title__'
    // link tags (canonical, hreflang) -- dedupe by rel+href
    if ('tagName' in tag) return `${(tag as any).rel}:${(tag as any).href}`
    return null
}

export function mergeMeta(
    matches: Parameters<MetaFunction>[0]['matches'],
    childMeta: MetaDescriptor[],
): MetaDescriptor[] {
    // Collect ALL parent meta from all matches (root first, most specific last).
    // Later matches override earlier ones by key, preventing duplicates.
    const parentByKey = new Map<string, MetaDescriptor>()
    for (const match of matches) {
        for (const tag of match.meta) {
            const key = metaKey(tag)
            if (key) parentByKey.set(key, tag)
        }
    }

    // Child meta wins over parent for any duplicate keys
    const childKeys = new Set(childMeta.flatMap((tag) => {
        const key = metaKey(tag)
        return key ? [key] : []
    }))

    const filteredParent = [...parentByKey.values()].filter((tag) => {
        const key = metaKey(tag)
        return key !== null && !childKeys.has(key)
    })

    return [...childMeta, ...filteredParent]
}
